import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type Mpegts from 'mpegts.js';
import { api, ApiError, formatRelative, formatUsd } from '../lib/api';
import { useAuth, scopedCan } from '../lib/auth';
import { matches, runningFirst } from '../lib/listing';
import { RecorderCard } from '../components/RecorderCard';
import { TestStreamCard, TestStreamDialog, useTestStreams } from '../components/TestStreamPanel';
import { AwsLink, Button, Card, Alert, Spinner, Empty, Modal, Field, Input, Select, StatTile, Confirm } from '../components/ui';
import {
  CodecFields, PictureFields, RtmpEditor, CostBreakdown, newRtmp, CODEC_LABEL, ClipUploader, formatClipTime,
  OutputFields, newOutput, duplicateOutput,
  type EncoderOptions, type Encode, type Rtmp, type CostEstimate, type Clip, type FlowOutputSpec,
} from '../components/encoder-forms';
import { FlowsSection, type FlowSummary } from './Flows';
import { ResourceGroups, SelectBox, SelectableRow, keyOf, type ResourceGroup, type Resource } from '../components/ResourceGroups';

type EncoderState =
  'creating' | 'starting' | 'running' | 'stopping' | 'stopped' | 'deleting' | 'deleted' | 'failed';

interface EncoderSummary {
  id: string;
  name: string;
  state: EncoderState;
  progress: string | null;
  error: string | null;
  createdAt: string;
  busy: boolean;
  spec: { encodes: Encode[]; dualIngest: boolean };
  estimate: { runningPerHour: number; rtmpPerHour: number; idlePerHour: number } | null;
}

interface FlowOutput {
  arn: string; name: string; protocol: string | null; enabled: boolean;
  address: string; mode: 'listener' | 'caller'; port: number | null; host: string | null;
  minLatency: number | null; streamId: string | null; description: string;
}

interface Alert { type: string; message: string }

interface RtmpView extends Rtmp {
  id: string; name: string; state: string; progress: string | null; error: string | null; alerts: Alert[];
  console?: string | null;
}

/** MediaLive's own complaints, in amber - worth seeing, not always a fault. */
function Alerts({ alerts }: { alerts: Alert[] }) {
  if (!alerts.length) return null;
  return (
    <div className="space-y-1">
      {alerts.map((a, i) => (
        <div key={i} className="text-xs" style={{ color: 'var(--color-warning)' }}>
          ⚠ {a.type}: <span className="text-ink-secondary">{a.message}</span>
        </div>
      ))}
    </div>
  );
}

interface EncoderDetailData {
  id: string;
  name: string;
  state: EncoderState;
  progress: string | null;
  error: string | null;
  busy: boolean;
  /** The signed-in user's level on this encoder: view | use | edit | full. */
  access?: string | null;
  /** When the stop timer stops it, or null for never. */
  stopAt?: string | null;
  /** The existing flow this encoder was added to, if it was. */
  fromFlow?: { id: string; name: string } | null;
  /** Which parts are being worked on: '*' the whole encoder, 'channel', 'rtmp:<id>'. */
  locks?: string[];
  spec: { encodes: Encode[]; dualIngest: boolean; audioPairs: number; dashboardPreview: boolean; overlayEnabled: boolean };
  channel: { id: string; name: string; state: string | null; activeInput: string | null; alerts: Alert[]; console?: string } | null;
  ingest: { name: string; az: string; status: string | null; port: number; role: string; address: string | null; active: boolean | null; external?: boolean; flowId?: string | null; console?: string | null }[];
  outputs: (Encode & {
    encode: number;
    flows: { name: string; az: string; leg: string; status: string | null; receiver: string | null; console?: string | null }[];
  })[];
  preview: boolean;
  media: {
    clips: (Clip & { id: string; ready: boolean })[];
    /** The clip MediaLive is playing now, or null for the live input. */
    onAir: string | null;
    cutAt: number | null;
  };
  overlay: { enabled: boolean; url: string; onAir: string | null };
  rtmp: RtmpView[];
  costs: {
    perHourNow: number; sessionUsd: number; estimatedToDate: number;
    estimate: CostEstimate | null;
    billed: { total: number; days: string[]; daily: number[]; byResource: { name: string; usd: number }[]; historyFrom: string | null };
  };
}

const TRANSITIONAL = ['creating', 'starting', 'stopping', 'deleting', 'updating'];

/** State as a word beside a dot - never the colour alone. */
function StateBadge({ state, progress }: { state: string; progress?: string | null }) {
  const s = state.toLowerCase();
  const colour =
    s === 'running' || s === 'active' ? 'var(--color-good)'
    : s === 'failed' || s === 'error' ? 'var(--color-critical)'
    : TRANSITIONAL.includes(s) || /ing$/.test(s) ? 'var(--color-warning)'
    : 'var(--color-ink-muted)';
  return (
    <span className="inline-flex items-center gap-1.5 text-xs">
      <span
        className={`h-2 w-2 shrink-0 rounded-full ${TRANSITIONAL.includes(s) ? 'animate-pulse-soft' : ''}`}
        style={{ background: colour }}
      />
      <span className="text-ink-secondary">{s}{progress ? ` — ${progress}` : ''}</span>
    </span>
  );
}

function Copyable({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <span className="inline-flex items-center gap-2">
      <code className="break-all font-mono text-xs">{text}</code>
      <button
        className="text-xs text-series-1 hover:underline"
        onClick={() => {
          void navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          });
        }}
      >
        {copied ? 'copied' : 'copy'}
      </button>
    </span>
  );
}

function Thumb({ src, label }: { src: string | null | undefined; label: string }) {
  return (
    <div className="aspect-video w-full overflow-hidden rounded bg-plane">
      {src ? (
        <img src={src} alt={label} className="h-full w-full object-contain" />
      ) : (
        <div className="flex h-full items-center justify-center text-xs text-ink-muted">no picture</div>
      )}
    </div>
  );
}

/** A connection dot: green connected, amber not, grey when nothing is known yet. */
function Dot({ on, title }: { on: boolean | null; title?: string }) {
  return (
    <span
      title={title}
      className="inline-block h-2 w-2 shrink-0 rounded-full"
      style={{ background: on == null ? 'var(--color-ink-muted)' : on ? 'var(--color-good)' : 'var(--color-warning)' }}
    />
  );
}

const metricOf = (m: Record<string, number> | undefined, re: RegExp): number | null => {
  const k = m && Object.keys(m).find((x) => re.test(x));
  return k ? m![k] : null;
};
const formatBitrate = (v: number) => (v >= 1e6 ? `${(v / 1e6).toFixed(2)} Mbit/s` : `${Math.round(v / 1e3)} kbit/s`);

interface FlowLive { running: boolean; source: Record<string, number>; outputs: Record<string, Record<string, number>>; at: string | null }

const describeEncode = (e: Encode) =>
  `${e.resolution}${e.fps} · ${CODEC_LABEL[e.codec] ?? e.codec} ${e.profile.toLowerCase().replace('_', ' ')} · ${e.mbps} Mbit · ${e.audioCodec} ${e.audioKbps}k`;

// ===========================================================================
// List
// ===========================================================================

export function Encoders() {
  const { can, user } = useAuth();
  const [list, setList] = useState<EncoderSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  // Groups: ticked rows, the groups, and the flows list (reported up by FlowsSection).
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [groups, setGroups] = useState<ResourceGroup[]>([]);
  const [flows, setFlows] = useState<FlowSummary[]>([]);
  const toggle = useCallback((key: string) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(key)) n.delete(key); else n.add(key);
    return n;
  }), []);
  // "Send a test stream": the dialog, and what it opens with when editing the one running.
  const tests = useTestStreams();
  const [testOpen, setTestOpen] = useState(false);
  const [testInitial, setTestInitial] = useState<Parameters<typeof TestStreamDialog>[0]['initial']>(null);
  // What is typed in the filter; kept in the address so a refresh keeps it.
  const [query, setQuery] = useState(() => new URLSearchParams(window.location.search).get('q') ?? '');
  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const u = new URL(window.location.href);
    if (query) u.searchParams.set('q', query); else u.searchParams.delete('q');
    window.history.replaceState(null, '', u);
  }, [query]);
  // Start typing anywhere on the page and it goes into the filter; Escape clears it.
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      const t = ev.target as HTMLElement | null;
      const typing = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable);
      if (ev.key === 'Escape' && t === searchRef.current) { setQuery(''); searchRef.current?.blur(); return; }
      if (typing || ev.ctrlKey || ev.metaKey || ev.altKey || ev.key.length !== 1) return;
      searchRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const load = useCallback(() => Promise.all([
    api.get<{ encoders: EncoderSummary[] }>('/encoders')
      .then((r) => setList(r.encoders))
      .catch((err: Error) => setError(err.message)),
    api.get<{ groups: ResourceGroup[] }>('/resource-groups')
      .then((r) => setGroups(r.groups))
      .catch(() => { /* groups are extra: the lists still show */ }),
  ]), []);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 10_000);
    return () => clearInterval(t);
  }, [load]);

  const resources = useMemo(() => {
    const m = new Map<string, Resource>();
    for (const e of list ?? []) m.set(`encoder:${e.id}`, { kind: 'encoder', id: e.id, name: e.name, state: e.state, progress: e.progress, error: e.error, busy: e.busy });
    for (const f of flows) m.set(`flow:${f.id}`, { kind: 'flow', id: f.id, name: f.name, state: f.state, progress: f.progress, error: f.error, busy: f.busy });
    return m;
  }, [list, flows]);
  // Grouped resources show in their group, not again in the lists.
  const grouped = useMemo(() => new Set(groups.flatMap((g) => g.members.map(keyOf))), [groups]);

  if (error) return <Alert tone="critical">{error}</Alert>;
  if (!list) return <Spinner label="Loading encoders…" />;

  const describe = (e: EncoderSummary) =>
    `${e.spec.encodes.map((x) => `${x.label} ${x.resolution}${x.fps} ${x.resolution}`).join(' ')} ${e.spec.dualIngest ? 'dual' : 'single'} ${e.state}`;
  const shown = runningFirst(list.filter((e) => !grouped.has(`encoder:${e.id}`) && matches(query, e.name, describe(e))));

  // A failed launch is removed without typing its name back: it is not on air,
  // and whatever it left in AWS goes with it.
  const failed = list.filter((e) => e.state === 'failed' && !e.busy);
  const remove = async (targets: EncoderSummary[]) => {
    const what = targets.length === 1 ? targets[0].name : `${targets.length} failed encoders`;
    if (!window.confirm(`Remove ${what}? Anything the launch left behind in AWS is deleted too.`)) return;
    setRemoving(true);
    try {
      for (const t of targets) await api.delete(`/encoders/${t.id}`, {});
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not remove it.');
    } finally {
      setRemoving(false);
      void load();
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Encoders</h1>
          <p className="text-sm text-ink-muted">MediaLive channels with their ingest and output flows, and - below - flows on their own. Costs are on each one and under Costs › Encoders.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {user?.isAdmin && (
            <Link to="/adopted" className="text-xs text-ink-secondary hover:underline">Adopted resources</Link>
          )}
          {can('resource:terminate') && failed.length > 1 && (
            <Button variant="ghost" loading={removing} onClick={() => void remove(failed)}>Remove {failed.length} failed</Button>
          )}
          {can('resource:start') && (
            <Button variant="secondary" onClick={() => { setTestInitial(null); setTestOpen(true); }} title="Send a test pattern or a recording from the hub to SRT or RTMP destinations, as it is">
              Send a test stream…
            </Button>
          )}
          {can('resource:create') && (
            <Link to="/launch/encoder"><Button variant="primary">Launch encoder</Button></Link>
          )}
        </div>
      </div>
      {tests.data && (
        <TestStreamCard data={tests.data} reload={() => void tests.load()} canUse={can('resource:start')} onEdit={(spec) => { setTestInitial(spec); setTestOpen(true); }} />
      )}
      <TestStreamDialog open={testOpen} onClose={() => setTestOpen(false)} data={tests.data} initial={testInitial} onStarted={() => void tests.load()} />
      <input
        ref={searchRef}
        value={query}
        onChange={(ev) => setQuery(ev.target.value)}
        placeholder="Type to filter encoders and flows - name, source, resolution…"
        className="w-full rounded-md border border-grid bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-muted focus:border-series-1 focus:outline-none"
        autoFocus
      />
      <ResourceGroups
        groups={groups}
        resources={resources}
        selected={selected}
        onToggle={toggle}
        onClearSelection={() => setSelected(new Set())}
        canEdit={can('resource:edit')}
        canStart={can('resource:start')}
        canStop={can('resource:stop')}
        canDelete={can('resource:terminate')}
        reload={() => void load()}
      />
      <Card>
        {list.length === 0 ? (
          <Empty title="No encoders">Launch one to build a channel, its ingest flows and its output flows together.</Empty>
        ) : shown.length === 0 ? (
          <div className="px-4 py-3 text-sm text-ink-muted">{query ? `No ungrouped encoder matches "${query}".` : 'Every encoder is in a group above.'}</div>
        ) : (
          <div className="divide-y divide-grid">
            {shown.map((e) => (
              <SelectableRow
                key={e.id}
                to={`/encoders/${e.id}`}
                box={can('resource:edit') && <SelectBox checked={selected.has(`encoder:${e.id}`)} onToggle={() => toggle(`encoder:${e.id}`)} label={e.name} />}
              >
                <div className="basis-full sm:basis-auto min-w-0 flex-1">
                  <div className="font-medium">{e.name}</div>
                  <div className="text-xs text-ink-muted">
                    {e.spec.encodes.map((x) => `${x.resolution}${x.fps} ${x.mbps}M${x.dualFlows ? ' ×2' : ''}`).join(' · ')}
                    {' · '}{e.spec.dualIngest ? 'dual ingest' : 'single ingest'}
                  </div>
                </div>
                {e.estimate && (
                  <div className="text-right text-xs">
                    <div className="tabular">{formatUsd(e.estimate.runningPerHour, 2)}/hr</div>
                    <div className="text-ink-muted">
                      {e.estimate.rtmpPerHour > 0 ? `+${formatUsd(e.estimate.rtmpPerHour, 2)} RTMP` : 'when running'}
                    </div>
                  </div>
                )}
                <StateBadge state={e.state} progress={TRANSITIONAL.includes(e.state) ? e.progress : null} />
                {e.state === 'failed' && !e.busy && can('resource:terminate') && (
                  <button
                    className="text-xs text-ink-muted hover:text-critical disabled:opacity-40"
                    disabled={removing}
                    onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); void remove([e]); }}
                  >
                    remove
                  </button>
                )}
                <span className="hidden w-24 text-right text-xs text-ink-muted sm:block">{formatRelative(e.createdAt)}</span>
              </SelectableRow>
            ))}
          </div>
        )}
      </Card>
      <FlowsSection
        query={query}
        onList={setFlows}
        hide={grouped}
        selected={can('resource:edit') ? selected : undefined}
        onToggle={toggle}
      />
    </div>
  );
}

// ===========================================================================
// Pieces of one encoder
// ===========================================================================

/**
 * The live preview: MPEG-TS relayed over a WebSocket as MediaLive sends it,
 * played by mpegts.js with latency chasing on, so it stays a fraction of a
 * second behind rather than drifting. Reconnects by itself when the channel
 * restarts or the connection drops.
 */
/**
 * Levels of every audio pair on the preview, from the server's ffmpeg: a
 * momentary loudness and the true peak of each channel, ten times a second.
 */
function AudioMeters({ id, pairs, running }: { id: string; pairs: number; running: boolean }) {
  const [levels, setLevels] = useState<{ pair: number; m: number; l: number; r: number }[]>([]);
  const [state, setState] = useState<'connecting' | 'live' | 'off'>('off');
  useEffect(() => {
    if (!running) { setLevels([]); setState('off'); return; }
    let ws: WebSocket | null = null;
    let cancelled = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let last = 0;
    const open = () => {
      if (cancelled) return;
      setState('connecting');
      const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
      ws = new WebSocket(`${scheme}://${window.location.host}/api/encoders/${id}/preview/meters`);
      ws.onmessage = (e) => {
        last = Date.now();
        try { const msg = JSON.parse(String(e.data)); setLevels(msg.pairs ?? []); setState('live'); } catch { /* not ours */ }
      };
      ws.onclose = () => { if (!cancelled) retry = setTimeout(open, 3000); };
      ws.onerror = () => ws?.close();
    };
    open();
    // Levels that stop arriving decay to nothing rather than freeze.
    const t = setInterval(() => { if (Date.now() - last > 1500) setLevels((l) => l.map((x) => ({ ...x, m: -120, l: -120, r: -120 }))); }, 500);
    return () => { cancelled = true; clearTimeout(retry); clearInterval(t); ws?.close(); };
  }, [id, running]);
  if (!running) return null;
  const width = (db: number) => `${Math.max(0, Math.min(100, ((Math.max(-60, db) + 60) / 60) * 100))}%`;
  const colour = (db: number) => (db > -3 ? 'var(--color-critical)' : db > -12 ? 'var(--color-warning)' : 'var(--color-good)');
  const rows = levels.length ? levels : Array.from({ length: pairs }, (_, i) => ({ pair: i + 1, m: -120, l: -120, r: -120 }));
  return (
    <div className="space-y-1.5 px-4 pb-4">
      <div className="flex items-center justify-between text-xs text-ink-muted">
        <span>Audio{pairs > 1 ? ` · ${pairs} pairs · the player plays pair 1` : ''}</span>
        <span>{state === 'live' ? 'true peak dBFS · momentary LUFS' : state === 'connecting' ? 'connecting to the meters…' : ''}</span>
      </div>
      {rows.map((p) => (
        <div key={p.pair} className="grid grid-cols-[3.5rem_1fr_4.5rem] items-center gap-2 text-xs">
          <span className="text-ink-secondary">Pair {p.pair}</span>
          <div className="space-y-0.5">
            {[p.l, p.r].map((db, i) => (
              <div key={i} className="h-1.5 overflow-hidden rounded bg-plane">
                <div className="h-full transition-[width] duration-100" style={{ width: width(db), background: colour(db) }} />
              </div>
            ))}
          </div>
          <span className="tabular text-right text-ink-muted">{p.m <= -70 ? '—' : `${p.m.toFixed(1)} LUFS`}</span>
        </div>
      ))}
    </div>
  );
}

function PreviewPlayer({ id, running, pairs }: { id: string; running: boolean; pairs: number }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [status, setStatus] = useState('connecting…');
  const [buffer, setBuffer] = useState<number | null>(null);

  useEffect(() => {
    if (!running) { setStatus('starts when the encoder is running'); return; }
    let player: Mpegts.Player | null = null;
    let cancelled = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let lastTime = -1;
    let stuckSince = Date.now();

    const stop = () => { try { player?.destroy(); } catch { /* already gone */ } player = null; };
    const again = (why: string) => {
      if (cancelled) return;
      setStatus(why);
      setBuffer(null);
      stop();
      clearTimeout(retry);
      retry = setTimeout(() => void start(), 3000);
    };

    const start = async () => {
      const { default: mpegts } = await import('mpegts.js');
      const video = ref.current;
      if (!video || cancelled) return;
      if (!mpegts.isSupported()) { setStatus('This browser cannot play the live preview.'); return; }
      mpegts.LoggingControl.enableAll = false;
      const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
      player = mpegts.createPlayer(
        { type: 'mpegts', isLive: true, url: `${scheme}://${window.location.host}/api/encoders/${id}/preview/live` },
        {
          enableWorker: false,
          enableStashBuffer: false,
          lazyLoad: false,
          autoCleanupSourceBuffer: true,
          // Catch up by playing a little faster, never by jumping: the old
          // chase seeked whenever it fell 0.8 s behind, a stutter a second.
          liveBufferLatencyChasing: false,
          liveSync: true,
          liveSyncMaxLatency: 1.2,
          liveSyncTargetLatency: 0.6,
          liveSyncPlaybackRate: 1.15,
        },
      );
      player.attachMediaElement(video);
      player.on(mpegts.Events.ERROR, () => again('preview dropped - reconnecting…'));
      player.on(mpegts.Events.LOADING_COMPLETE, () => again('preview ended - reconnecting…'));
      player.load();
      video.muted = true;
      void Promise.resolve(player.play()).catch(() => undefined);
      setStatus('waiting for pictures…');
      lastTime = -1;
      stuckSince = Date.now();
    };

    // How far the picture is behind the newest data received, and a restart
    // if playback has stalled (a gap in the stream, a sleeping tab).
    const tick = setInterval(() => {
      const v = ref.current;
      if (!v || !player) return;
      const end = v.buffered.length ? v.buffered.end(v.buffered.length - 1) : 0;
      if (v.currentTime !== lastTime) {
        lastTime = v.currentTime;
        stuckSince = Date.now();
        if (end) { setStatus(''); setBuffer(Math.max(0, end - v.currentTime)); }
      } else if (Date.now() - stuckSince > 8000) {
        again('no pictures - reconnecting…');
      }
      // Speeding up covers ordinary drift; only a big gap (a background tab
      // that was asleep) is skipped outright.
      if (end - v.currentTime > 4) v.currentTime = end - 0.6;
    }, 500);

    void start();
    return () => { cancelled = true; clearTimeout(retry); clearInterval(tick); stop(); };
  }, [id, running]);

  return (
    <div>
      <div className="space-y-2 p-4">
        <video ref={ref} controls playsInline muted className="aspect-video w-full rounded bg-black" />
        <div className="flex justify-between gap-3 text-xs text-ink-muted">
          <span>{status || '540p live - unmute with the player controls'}</span>
          {buffer != null && !status && <span className="tabular">player buffer {buffer.toFixed(2)} s</span>}
        </div>
      </div>
      <AudioMeters id={id} pairs={pairs} running={running} />
    </div>
  );
}

/**
 * Cut to a clip, see what is on air and how long it has left, and cut back.
 * A clip returns to live by itself when it ends; Back to live cuts it short.
 */
function ClipDeck({ id, media, running, canUse, onDone }: {
  id: string;
  media: EncoderDetailData['media'];
  running: boolean;
  canUse: boolean;
  onDone: (err?: string) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  const onAir = media.clips.find((c) => c.id === media.onAir) ?? null;

  useEffect(() => {
    if (!onAir) return;
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, [onAir]);

  const left = onAir?.durationSec != null && media.cutAt ? onAir.durationSec - (now - media.cutAt) / 1000 : null;

  const cut = async (clip: string | null) => {
    setBusy(clip ?? 'live');
    try {
      await api.post(`/encoders/${id}/media/cut`, { clip });
      onDone();
    } catch (err) {
      onDone(err instanceof ApiError ? err.message : 'The cut did not happen.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-3 p-4">
      <div className="flex flex-wrap items-center gap-3">
        {onAir ? (
          <span className="inline-flex items-center gap-2 rounded px-2 py-1 text-sm font-medium text-white" style={{ background: 'var(--color-critical)' }}>
            ● CLIP: {onAir.name}
            {left != null && <span className="tabular font-normal">{formatClipTime(left)} left</span>}
          </span>
        ) : (
          <span className="inline-flex items-center gap-2 rounded px-2 py-1 text-sm font-medium text-white" style={{ background: 'var(--color-good)' }}>
            ● LIVE
          </span>
        )}
        {onAir && canUse && (
          <Button variant="primary" loading={busy === 'live'} disabled={!running || !!busy} onClick={() => void cut(null)}>Back to live</Button>
        )}
        <span className="text-xs text-ink-muted">
          {onAir ? 'Cuts back to live by itself when the clip ends.' : running ? 'Press a clip to cut to it.' : 'The encoder has to be running.'}
        </span>
      </div>
      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {media.clips.map((c) => {
          const playing = c.id === media.onAir;
          return (
            <button
              key={c.id}
              disabled={!canUse || !running || !c.ready || !!busy}
              onClick={() => void cut(c.id)}
              className={`flex items-center justify-between gap-2 rounded px-3 py-2.5 text-left text-sm ring-1 transition disabled:opacity-50 ${
                playing ? '' : 'ring-hairline hover:bg-raised'
              }`}
              style={playing ? { boxShadow: '0 0 0 2px var(--color-critical)' } : undefined}
              title={playing ? 'Playing - press to start it again from the top' : `Cut to ${c.name}`}
            >
              <span className="min-w-0">
                <span className="block truncate font-medium">{c.name}</span>
                <span className="text-xs text-ink-muted">
                  {formatClipTime(c.durationSec)}{!c.ready && ' · not attached yet'}
                </span>
              </span>
              <span className="shrink-0 text-xs text-series-1">{busy === c.id ? 'cutting…' : playing ? 'restart' : 'cut to'}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Put a page on air, switch pages, or take it off - no restart. */
function OverlayControl({ id, overlay, running, canUse, onDone }: {
  id: string;
  overlay: EncoderDetailData['overlay'];
  running: boolean;
  canUse: boolean;
  onDone: (msg: string | null, err?: string) => void;
}) {
  const [url, setUrl] = useState(overlay.onAir ?? overlay.url);
  const [busy, setBusy] = useState(false);
  const send = async (on: boolean) => {
    setBusy(true);
    try {
      await api.post(`/encoders/${id}/overlay`, { on, url });
      onDone(on ? 'Overlay on air.' : 'Overlay off air.');
    } catch (err) {
      onDone(null, err instanceof ApiError ? err.message : 'Could not change the overlay.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2 border-t border-grid p-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">HTML overlay</span>
        {overlay.onAir
          ? <span className="text-xs" style={{ color: 'var(--color-good)' }}>● on air</span>
          : <span className="text-xs text-ink-muted">○ off air</span>}
      </div>
      <div className="flex flex-wrap gap-2">
        <Input className="min-w-0 flex-1" value={url} onChange={(e) => setUrl(e.target.value.trim())} placeholder="https://…" disabled={!canUse} />
        {canUse && (
          <>
            <Button variant="primary" loading={busy} disabled={!running || !url} onClick={() => void send(true)}>
              {overlay.onAir && overlay.onAir !== url ? 'Switch to this page' : 'Put on air'}
            </Button>
            <Button disabled={!running || !overlay.onAir || busy} onClick={() => void send(false)}>Take off air</Button>
          </>
        )}
      </div>
      <p className="text-xs text-ink-muted">
        Applies within a second or two, on every encode of this channel. {!running && 'The encoder has to be running.'}
      </p>
    </div>
  );
}

/** A flow's outputs: address, on/off, remove, and a form to add another. */
function FlowOutputs({ encoderId, flow, canEdit, canSwitch, running, feedLabel = 'Feed from MediaLive' }: {
  encoderId: string; flow: string; canEdit: boolean; canSwitch: boolean; running: boolean;
  /** Who feeds this flow: MediaLive for an output flow, the venue for an ingest flow. */
  feedLabel?: string;
}) {
  const [outs, setOuts] = useState<FlowOutput[] | null>(null);
  const [live, setLive] = useState<FlowLive | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The one form open: a new output (blank or a duplicate), or an existing one being edited.
  const [form, setForm] = useState<{ arn?: string; value: FlowOutputSpec } | null>(null);
  const [removing, setRemoving] = useState<FlowOutput | null>(null);
  const [busy, setBusy] = useState(false);
  const url = `/encoders/${encoderId}/flows/${encodeURIComponent(flow)}/outputs`;

  const load = useCallback(async () => {
    try {
      setOuts((await api.get<{ outputs: FlowOutput[] }>(url)).outputs);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not list outputs.');
    }
  }, [url]);
  useEffect(() => { void load(); }, [load]);

  // Who is taking each output, and whether MediaLive is feeding the flow:
  // CloudWatch, every five seconds while the encoder runs.
  useEffect(() => {
    if (!running) { setLive(null); return; }
    let cancelled = false;
    const grab = () => api.get<{ live: FlowLive }>(`/encoders/${encoderId}/flows/${encodeURIComponent(flow)}/live`)
      .then((r) => { if (!cancelled) setLive(r.live); })
      .catch(() => undefined);
    void grab();
    const t = setInterval(grab, 5000);
    return () => { cancelled = true; clearInterval(t); };
  }, [encoderId, flow, running]);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try { await fn(); await load(); } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    } finally { setBusy(false); }
  };

  const specOf = (o: FlowOutput): FlowOutputSpec => ({
    name: o.name, mode: o.mode, port: o.port ?? 7000, host: o.host ?? '', streamId: o.streamId ?? '', minLatency: o.minLatency ?? 1000,
  });
  const editingName = form?.arn ? outs?.find((o) => o.arn === form.arn)?.name : undefined;

  const editor = form && (
    <div className="space-y-2 rounded border border-grid p-3">
      <OutputFields value={form.value} onChange={(value) => setForm({ ...form, value })} />
      {form.arn && (
        <p className="text-xs text-ink-muted">
          Applied straight away - whoever is connected is dropped and reconnects.
          {editingName && form.value.name !== editingName && (
            <> <b>A new name re-adds the output</b> under it with the same settings (MediaConnect cannot rename one in place).</>
          )}
        </p>
      )}
      <div className="flex gap-2">
        <Button variant="primary" loading={busy} disabled={!form.value.name} onClick={() => void act(async () => {
          const { enabled: _e, ...change } = form.value;
          if (form.arn) await api.put(url, { arn: form.arn, ...change });
          else await api.post(url, change);
          setForm(null);
        })}>{form.arn ? 'Save' : 'Add output'}</Button>
        <Button variant="ghost" onClick={() => setForm(null)}>Cancel</Button>
      </div>
    </div>
  );

  const feedConnected = running && live ? metricOf(live.source, /^(Source)?Connected$/) : null;
  const feedBitrate = running && live ? metricOf(live.source, /^(Source)?BitRate$/) : null;

  const statusOf = (o: FlowOutput) => {
    if (!o.enabled) return <span className="text-ink-muted">switched off</span>;
    if (!running) return <span className="text-ink-muted">encoder stopped</span>;
    const m = live?.outputs[o.name];
    const connected = metricOf(m, /Connected$/);
    const bitrate = metricOf(m, /BitRate$/);
    const on = connected == null ? null : connected >= 0.5;
    return (
      <span className="inline-flex items-center gap-1.5">
        <Dot on={on} />
        <span className={on ? 'text-ink' : 'text-ink-secondary'}>
          {on == null ? 'no reading yet' : on ? 'connected' : o.mode === 'listener' ? 'nobody pulling' : 'not connected'}
          {bitrate != null && bitrate > 0 && ` · ${formatBitrate(bitrate)}`}
        </span>
      </span>
    );
  };

  return (
    <div className="space-y-2">
      {error && <div className="text-xs" style={{ color: 'var(--color-critical)' }}>{error}</div>}
      <div className="flex items-center gap-1.5 text-xs">
        <Dot on={feedConnected == null ? null : feedConnected >= 0.5} />
        <span className="text-ink-secondary">
          {feedLabel}: {!running ? 'encoder stopped' : feedConnected == null ? 'no reading yet' : feedConnected >= 0.5 ? 'connected' : 'not connected'}
          {feedBitrate != null && feedBitrate > 0 && ` · ${formatBitrate(feedBitrate)}`}
        </span>
      </div>
      {outs === null ? <div className="text-xs text-ink-muted">loading outputs…</div> : outs.length === 0 ? (
        <div className="text-xs text-ink-muted">No SRT outputs on this flow yet.</div>
      ) : (
        <div className="overflow-x-auto"><table className="w-full text-xs">
          <thead>
            <tr className="border-b border-grid text-left text-ink-muted">
              <th className="py-1.5 pr-3 font-medium">Output</th>
              <th className="hidden py-1.5 pr-3 font-medium sm:table-cell">Mode</th>
              <th className="py-1.5 pr-3 font-medium">Address</th>
              <th className="hidden py-1.5 pr-3 font-medium sm:table-cell">Latency</th>
              <th className="py-1.5 pr-3 font-medium">Status</th>
              <th className="py-1.5" />
            </tr>
          </thead>
          <tbody>
            {outs.map((o) => (
              form?.arn === o.arn ? (
                <tr key={o.arn}><td colSpan={6} className="py-2">{editor}</td></tr>
              ) : (
                <tr key={o.arn} className="border-b border-grid last:border-0">
                  <td className={`py-2 pr-3 font-medium ${o.enabled ? '' : 'text-ink-muted line-through'}`}>{o.name}</td>
                  <td className="hidden py-2 pr-3 text-ink-secondary sm:table-cell">{o.mode === 'listener' ? 'Listener · they pull' : 'Caller · we push'}</td>
                  <td className="py-2 pr-3">
                    <Copyable text={o.address} />
                    {o.streamId && <span className="ml-2 text-ink-muted">id {o.streamId}</span>}
                  </td>
                  <td className="hidden py-2 pr-3 tabular text-ink-secondary sm:table-cell">{o.minLatency != null ? `${o.minLatency} ms` : '—'}</td>
                  <td className="py-2 pr-3">{statusOf(o)}</td>
                  <td className="py-2 text-right"><div className="flex flex-wrap justify-end gap-x-3 gap-y-1">
                    {canSwitch && (
                      <button className="mr-3 text-series-1 hover:underline disabled:opacity-40" disabled={busy}
                        onClick={() => void act(() => api.patch(url, { arn: o.arn, enabled: !o.enabled }))}>
                        {o.enabled ? 'turn off' : 'turn on'}
                      </button>
                    )}
                    {canEdit && (
                      <>
                        <button className="mr-3 text-series-1 hover:underline disabled:opacity-40" disabled={busy}
                          onClick={() => setForm({ arn: o.arn, value: specOf(o) })}>edit</button>
                        <button className="mr-3 text-series-1 hover:underline disabled:opacity-40" disabled={busy}
                          onClick={() => setForm({ value: duplicateOutput(specOf(o), outs.map((x) => x.name)) })}>duplicate</button>
                        <button className="text-ink-muted hover:text-critical disabled:opacity-40" disabled={busy}
                          onClick={() => setRemoving(o)}>remove</button>
                      </>
                    )}
                  </div></td>
                </tr>
              )
            ))}
          </tbody>
        </table></div>
      )}
      {form && !form.arn && editor}
      {canEdit && !form && (
        <button className="text-xs text-series-1 hover:underline" onClick={() => setForm({ value: newOutput() })}>+ add SRT output</button>
      )}
      <Confirm
        open={!!removing}
        title={`Remove ${removing?.name ?? ''}?`}
        message="Whoever is taking this output loses the feed, and its address stops working."
        confirmLabel="Remove"
        danger
        busy={busy}
        onClose={() => setRemoving(null)}
        onConfirm={() => { const o = removing!; setRemoving(null); void act(() => api.delete(url, { arn: o.arn })); }}
      />
    </div>
  );
}

// ===========================================================================
// MediaLive's own settings, as JSON
// ===========================================================================

/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * One encode's part of the channel: its video and audio descriptions, its
 * output group and where that sends, plus the channel-wide settings that
 * shape every encode. The names are the ones buildChannel gives them.
 */
function encodeSlice(channel: any, encode: number) {
  const es = channel?.EncoderSettings ?? {};
  const v = `video_e${encode + 1}`;
  return {
    VideoDescription: (es.VideoDescriptions ?? []).find((d: any) => d.Name === v) ?? null,
    AudioDescriptions: (es.AudioDescriptions ?? []).filter((d: any) => String(d.Name).startsWith(`audio_e${encode + 1}_`)),
    OutputGroup: (es.OutputGroups ?? []).find((g: any) => (g.Outputs ?? []).some((o: any) => o.VideoDescriptionName === v)) ?? null,
    Destinations: (channel?.Destinations ?? []).filter((d: any) => String(d.Id).startsWith(`dest-e${encode + 1}-`)),
    ChannelWide: {
      GlobalConfiguration: es.GlobalConfiguration,
      TimecodeConfig: es.TimecodeConfig,
      AvailBlanking: es.AvailBlanking,
      MotionGraphicsConfiguration: es.MotionGraphicsConfiguration,
      InputAttachments: channel?.InputAttachments,
      InputSpecification: channel?.InputSpecification,
    },
  };
}
/* eslint-enable @typescript-eslint/no-explicit-any */

function ChannelSettings({ encoderId, target, onClose }: {
  encoderId: string;
  /** An encode of the main channel, or an RTMP encode's own channel. */
  target: { title: string; encode?: number; rtmpId?: string } | null;
  onClose: () => void;
}) {
  const [channel, setChannel] = useState<unknown>(null);
  const [error, setError] = useState<string | null>(null);
  const [whole, setWhole] = useState(false);
  const [q, setQ] = useState('');
  const [at, setAt] = useState(0);
  const [copied, setCopied] = useState(false);
  const pre = useRef<HTMLPreElement>(null);

  useEffect(() => {
    if (!target) return;
    setChannel(null); setError(null); setWhole(target.encode == null); setQ(''); setAt(0);
    api.get<{ channel: unknown }>(`/encoders/${encoderId}/medialive${target.rtmpId ? `?rtmp=${target.rtmpId}` : ''}`)
      .then((r) => setChannel(r.channel))
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Could not read the channel.'));
  }, [encoderId, target]);

  const text = useMemo(() => {
    if (!channel) return '';
    return JSON.stringify(whole || target?.encode == null ? channel : encodeSlice(channel, target.encode), null, 2);
  }, [channel, whole, target]);

  // Every match, as [line, start]; the lines with one get a mark each.
  const needle = q.trim().toLowerCase();
  const lines = useMemo(() => text.split('\n'), [text]);
  const hits = useMemo(() => {
    if (!needle) return [] as [number, number][];
    const out: [number, number][] = [];
    lines.forEach((l, i) => {
      const low = l.toLowerCase();
      for (let k = low.indexOf(needle); k >= 0; k = low.indexOf(needle, k + needle.length)) out.push([i, k]);
    });
    return out;
  }, [lines, needle]);
  const cur = hits.length ? ((at % hits.length) + hits.length) % hits.length : -1;

  useEffect(() => {
    if (cur < 0) return;
    pre.current?.querySelector(`[data-hit="${cur}"]`)?.scrollIntoView({ block: 'center' });
  }, [cur, hits]);

  let n = 0;
  const body = lines.map((l, i) => {
    const low = l.toLowerCase();
    if (!needle || !low.includes(needle)) return <div key={i}>{l || ' '}</div>;
    const parts: ReactNode[] = [];
    let from = 0;
    for (let k = low.indexOf(needle); k >= 0; k = low.indexOf(needle, k + needle.length)) {
      parts.push(l.slice(from, k));
      const h = n++;
      parts.push(
        <mark key={k} data-hit={h} className={h === cur ? 'bg-series-1 text-white' : 'bg-warning/40 text-ink'}>
          {l.slice(k, k + needle.length)}
        </mark>,
      );
      from = k + needle.length;
    }
    parts.push(l.slice(from));
    return <div key={i}>{parts}</div>;
  });

  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* not over http */ }
  };

  return (
    <Modal open={!!target} onClose={onClose} title={target ? `${target.title} - MediaLive settings` : ''} width="max-w-5xl">
      {error ? <Alert tone="critical">{error}</Alert> : !channel ? <Spinner label="Reading the channel…" /> : (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              className="max-w-xs"
              placeholder="search, e.g. GopSize, Bitrate, Afd"
              value={q}
              autoFocus
              onChange={(e) => { setQ(e.target.value); setAt(0); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setAt((a) => a + (e.shiftKey ? -1 : 1)); } }}
            />
            <span className="text-xs tabular text-ink-muted">
              {needle ? (hits.length ? `${cur + 1} of ${hits.length}` : 'no matches') : `${lines.length} lines`}
            </span>
            {hits.length > 1 && (
              <>
                <Button variant="ghost" onClick={() => setAt((a) => a - 1)}>↑</Button>
                <Button variant="ghost" onClick={() => setAt((a) => a + 1)}>↓</Button>
              </>
            )}
            <span className="flex-1" />
            {target?.encode != null && (
              <div className="inline-flex rounded-md text-xs ring-1 ring-hairline">
                {[false, true].map((w) => (
                  <button key={String(w)} type="button" onClick={() => { setWhole(w); setAt(0); }}
                    className={`px-2.5 py-1 ${w ? 'rounded-r-md' : 'rounded-l-md'} ${whole === w ? 'bg-series-1 text-white' : 'text-ink-secondary hover:bg-raised'}`}>
                    {w ? 'Whole channel' : 'This encode'}
                  </button>
                ))}
              </div>
            )}
            <Button variant="ghost" onClick={() => void copy()}>{copied ? 'Copied' : 'Copy JSON'}</Button>
          </div>
          <pre ref={pre} className="max-h-[60vh] overflow-auto rounded bg-plane p-3 font-mono text-xs leading-5">{body}</pre>
          <p className="text-xs text-ink-muted">
            Read from MediaLive just now, as it has the channel - including the defaults the form never sets. Stream
            keys are masked. Enter for the next match, Shift+Enter for the one before.
          </p>
        </div>
      )}
    </Modal>
  );
}

// ===========================================================================
// One encoder
// ===========================================================================

export function EncoderDetail() {
  const { id } = useParams<{ id: string }>();
  const auth = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<EncoderDetailData | null>(null);
  const [opts, setOpts] = useState<EncoderOptions | null>(null);
  const [thumbs, setThumbs] = useState<{ channel: string | null; channelSource?: string | null; flows: Record<string, string | null> } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [acting, setActing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState<{ audioPairs: number; encodes: Encode[]; dashboardPreview: boolean; overlayEnabled: boolean } | null>(null);
  const [portEdit, setPortEdit] = useState<{ index: number; port: number } | null>(null);
  const [rtmpEdit, setRtmpEdit] = useState<{ id?: string; label?: string; value: Rtmp } | null>(null);
  const [rtmpAsk, setRtmpAsk] = useState<{ r: RtmpView; action: 'stop' | 'delete' } | null>(null);
  const [stopAsk, setStopAsk] = useState(false);
  const [encodeAsk, setEncodeAsk] = useState<{ encode: number; label: string; flows: string[] } | null>(null);
  const [renameAsk, setRenameAsk] = useState(false);
  const [newName, setNewName] = useState('');
  const [showKeys, setShowKeys] = useState(false);
  const [clipEdit, setClipEdit] = useState<Clip[] | null>(null);
  const [clipUploading, setClipUploading] = useState(false);
  const [settingsOf, setSettingsOf] = useState<{ title: string; encode?: number; rtmpId?: string } | null>(null);
  const [saveTpl, setSaveTpl] = useState<{ name: string; description: string; savedId?: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get<{ encoder: EncoderDetailData }>(`/encoders/${id}`);
      setData(r.encoder);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load this encoder.');
    }
  }, [id]);

  useEffect(() => { api.get<EncoderOptions>('/encoders/options').then(setOpts).catch(() => undefined); }, []);

  const running = data?.state === 'running';
  const can = scopedCan(auth.can, data?.access);
  // Each part moves on its own: the encoder and its main channel (moving),
  // and each RTMP encode (rtmpMoving) - one being re-keyed no longer greys out
  // the others or the encoder.
  const moving = !!data && (data.busy || TRANSITIONAL.includes(data.state));
  const wholeBusy = !!data && ((data.locks ?? []).includes('*') || ['creating', 'deleting'].includes(data.state));
  const rtmpMoving = (r: RtmpView) => wholeBusy || TRANSITIONAL.includes(r.state) || (data?.locks ?? []).includes(`rtmp:${r.id}`);
  const anyMoving = moving || (!!data && data.rtmp.some((r) => TRANSITIONAL.includes(r.state)));

  // Quicker while a clip plays, so the page sees it cut back to live.
  const clipPlaying = !!data?.media.onAir;
  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), anyMoving ? 4000 : clipPlaying ? 2000 : 15000);
    return () => clearInterval(t);
  }, [load, anyMoving, clipPlaying]);

  // Pictures every five seconds while anything could be showing one.
  useEffect(() => {
    if (!data || data.state === 'deleted' || data.state === 'stopped') return;
    let cancelled = false;
    const grab = () =>
      api.get<{ channel: string | null; channelSource?: string | null; flows: Record<string, string | null> }>(`/encoders/${id}/thumbnails`)
        .then((t) => { if (!cancelled) setThumbs(t); })
        .catch(() => undefined);
    void grab();
    const t = setInterval(() => void grab(), 5000);
    return () => { cancelled = true; clearInterval(t); };
  }, [id, data?.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    setActing(true);
    setError(null);
    try {
      await fn();
      if (ok) setNotice(ok);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    } finally {
      setActing(false);
    }
  };

  if (!data) return error ? <Alert tone="critical">{error}</Alert> : <Spinner label="Loading encoder…" />;
  const c = data.costs;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/encoders" className="text-xs text-ink-muted hover:underline">← Encoders</Link>
          <h1 className="mt-1 text-lg font-semibold">{data.name}</h1>
          <div className="mt-1"><StateBadge state={data.state} progress={moving ? data.progress : null} /></div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {data.state === 'running' && can('resource:stop') && (
            <label className="flex items-center gap-1.5 text-xs text-ink-muted" title="Stops the whole encoder after this long, as if Stop had been pressed">
              {data.stopAt ? `Stops ${new Date(data.stopAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : 'Stop timer'}
              <select
                className="rounded border border-grid bg-plane px-1 py-1 text-xs text-ink"
                value=""
                onChange={(e) => {
                  const v = e.target.value; if (v === '') return;
                  void run(() => api.post(`/encoders/${id}/timer`, { hours: Number(v) }), Number(v) ? `It will stop in ${v} hour${v === '1' ? '' : 's'}.` : 'Timer cleared - it runs until stopped.');
                }}
              >
                <option value="">{data.stopAt ? 'change…' : 'never'}</option>
                {[1, 2, 3, 4, 6, 8, 12, 24].map((h) => <option key={h} value={h}>in {h} h</option>)}
                {data.stopAt && <option value="0">never</option>}
              </select>
            </label>
          )}
          {can('resource:create') && (
            <Button variant="ghost" onClick={() => setSaveTpl({ name: data.name, description: '' })}>Save as template</Button>
          )}
          {data.state === 'stopped' && can('resource:start') && (
            <Button variant="primary" loading={acting} disabled={moving} onClick={() => void run(() => api.post(`/encoders/${id}/start`))}>Start</Button>
          )}
          {(data.state === 'running' || data.state === 'failed') && can('resource:stop') && (
            <Button loading={acting} disabled={moving} onClick={() => setStopAsk(true)}>Stop</Button>
          )}
          {data.state === 'stopped' && can('resource:edit') && (
            <Button variant="ghost" disabled={moving} onClick={() => { setNewName(data.name); setRenameAsk(true); }}>Rename</Button>
          )}
          {data.state !== 'deleted' && can('resource:terminate') && (
            <Button variant="ghost" disabled={moving} onClick={() => setDeleting(true)}>Delete</Button>
          )}
        </div>
      </div>

      {error && <Alert tone="critical">{error}</Alert>}
      {notice && <Alert tone="good">{notice}</Alert>}
      {data.error && data.state === 'failed' && <Alert tone="critical" title="Something went wrong">{data.error}</Alert>}

      {/* --- What it is costing --- */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Costing now"
          value={`${formatUsd(c.perHourNow, 2)}/hr`}
          sub={running
            ? 'estimated, while running'
            : `stopped - ${formatUsd(c.estimate?.idlePerHour ?? 0, 3)}/hr idle${c.estimate ? `, ${formatUsd(c.estimate.mainPerHour, 2)}/hr when running` : ''}`}
          tone={c.perHourNow > 5 ? 'warning' : 'default'}
        />
        <StatTile label="This session" value={formatUsd(c.sessionUsd)} sub="estimated since it started" />
        <StatTile label="Estimated to date" value={formatUsd(c.estimatedToDate)} sub="every run, at launch-time rates" />
        <StatTile
          label="Billed"
          value={formatUsd(c.billed.total)}
          sub={c.billed.historyFrom ? `Cost Explorer, from ${c.billed.historyFrom} - lags a day` : 'Cost Explorer - appears after a day'}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card
          title={data.channel?.name ?? 'MediaLive channel'}
          action={
            <div className="flex items-center gap-2">
              <AwsLink href={data.channel?.console} what="channel" />
              {can('resource:edit') && opts && (
                <Button variant="ghost" disabled={moving} onClick={() => setEditing({
                  audioPairs: data.spec.audioPairs,
                  encodes: data.spec.encodes.map((e) => ({ ...e })),
                  dashboardPreview: data.spec.dashboardPreview,
                  overlayEnabled: data.spec.overlayEnabled,
                })}>Edit encoding</Button>
              )}
            </div>
          }
        >
          <div className="space-y-2 p-4">
            <Thumb src={thumbs?.channel} label="MediaLive output" />
            <div className="flex flex-wrap gap-x-4 text-xs text-ink-muted">
              <span>Channel {data.channel?.state?.toLowerCase() ?? '—'}</span>
              {data.channel?.activeInput && <span>on the {data.channel.activeInput} input</span>}
              <span>{data.spec.audioPairs} audio pair{data.spec.audioPairs === 1 ? '' : 's'}</span>
              {thumbs?.channelSource && thumbs.channelSource !== 'channel' && (
                <span>picture: {thumbs.channelSource} as MediaLive sends it</span>
              )}
            </div>
            <Alerts alerts={data.channel?.alerts ?? []} />
          </div>
          {data.overlay.enabled && (
            <OverlayControl
              id={data.id} overlay={data.overlay} running={running} canUse={can('resource:start')}
              onDone={(msg, err) => { if (err) setError(err); else { setNotice(msg); void load(); } }}
            />
          )}
        </Card>

        {data.preview ? (
          <Card title="Dashboard preview"><PreviewPlayer id={data.id} running={running} pairs={data.spec.audioPairs} /></Card>
        ) : (
          <Card title="Cost per hour, running">
            <div className="p-4">{c.estimate ? <CostBreakdown est={c.estimate} /> : <span className="text-xs text-ink-muted">Pricing unavailable.</span>}</div>
          </Card>
        )}
      </div>

      {(data.media.clips.length > 0 || can('resource:edit')) && (
        <Card
          title="Media clips"
          action={can('resource:edit') && (
            <Button variant="ghost" disabled={moving} onClick={() => setClipEdit(data.media.clips.map(({ id: cid, name, key, durationSec }) => ({ id: cid, name, key, durationSec })))}>
              Edit clips
            </Button>
          )}
        >
          {data.media.clips.length === 0 ? (
            <p className="p-4 text-sm text-ink-muted">
              None. Upload stings or breaks under Edit clips to cut to them from here - each returns to live by itself when it ends.
            </p>
          ) : (
            <ClipDeck
              id={data.id} media={data.media} running={running} canUse={can('resource:start')}
              onDone={(err) => {
                if (err) setError(err);
                // MediaLive takes a moment to switch; look again shortly.
                void load(); setTimeout(() => void load(), 1500); setTimeout(() => void load(), 4000);
              }}
            />
          )}
        </Card>
      )}

      {data.state !== 'deleted' && data.state !== 'deleting' && (
        <RecorderCard kind="encoder" id={data.id} canUse={can('resource:start')} canEdit={can('resource:edit')} />
      )}

      <Card title="Ingest">
        <div className="grid gap-4 p-4 md:grid-cols-2">
          {data.ingest.map((f, i) => (
            <div key={f.name} className="space-y-2">
              <Thumb src={thumbs?.flows[f.name]} label={f.name} />
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium">{f.name}</span>
                <span className="flex items-center gap-2">
                  <AwsLink href={f.console} what="flow" />
                  <StateBadge state={f.status ?? 'unknown'} />
                </span>
              </div>
              <div className="text-xs text-ink-muted">
                {f.role} · {f.az}
                {f.active === true && <span style={{ color: 'var(--color-good)' }}> · ● on air</span>}
              </div>
              {f.external && (
                <div className="text-xs text-ink-secondary">
                  A flow of its own, which this encoder was added to.{' '}
                  {f.flowId && <Link className="text-series-1 hover:underline" to={`/flows/${f.flowId}`}>Its source and outputs are on its own page →</Link>}
                  {' '}Stopping or deleting the encoder leaves it running.
                </div>
              )}
              {f.address && !f.external && (
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-ink-muted">Venue sends SRT (caller) to</span>
                  <Copyable text={f.address} />
                  {can('resource:edit') && (
                    <button className="text-series-1 hover:underline" onClick={() => setPortEdit({ index: i, port: f.port })}>change port</button>
                  )}
                </div>
              )}
              {!f.external && <FlowOutputs encoderId={data.id} flow={f.name} canEdit={can('resource:edit')} canSwitch={can('resource:stop')} running={running} feedLabel="Feed from the venue" />}
            </div>
          ))}
        </div>
      </Card>

      {data.outputs.map((o) => (
        <Card
          key={o.encode}
          title={`Output ${o.label}`}
          action={data.channel && (
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setSettingsOf({ title: o.label, encode: o.encode })}>MediaLive settings</Button>
              {o.encode > 0 && can('resource:edit') && (
                <Button variant="ghost" disabled={moving} onClick={() => setEncodeAsk({ encode: o.encode, label: o.label, flows: o.flows.map((f) => f.name) })}>Remove</Button>
              )}
            </div>
          )}
        >
          <div className="divide-y divide-grid">
            <div className="px-4 py-2 text-xs text-ink-muted">{describeEncode(o)}{o.dualFlows ? ' · two flows, one per zone' : ''}</div>
            {o.flows.map((f) => (
              <div key={f.name} className="space-y-3 p-4">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="w-32 shrink-0"><Thumb src={thumbs?.flows[f.name]} label={f.name} /></div>
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{f.name}</span>
                      <StateBadge state={f.status ?? 'unknown'} />
                      <span className="text-xs text-ink-muted">{f.leg} leg · {f.az}</span>
                      <AwsLink href={f.console} what="flow" />
                    </div>
                    <div className="text-xs text-ink-muted">The flow this encode is sent to; each row below is a receiver's way in.</div>
                  </div>
                </div>
                <FlowOutputs encoderId={data.id} flow={f.name} canEdit={can('resource:edit')} canSwitch={can('resource:stop')} running={running} />
              </div>
            ))}
          </div>
        </Card>
      ))}

      <Card
        title="RTMP encodes"
        action={
          <div className="flex gap-2">
            {data.rtmp.length > 0 && (
              <Button variant="ghost" onClick={() => setShowKeys((v) => !v)}>{showKeys ? 'Hide keys' : 'Show keys'}</Button>
            )}
            {can('resource:edit') && (
              <Button variant="ghost" disabled={wholeBusy} onClick={() => setRtmpEdit({ value: newRtmp() })}>+ Add RTMP</Button>
            )}
          </div>
        }
      >
        {data.rtmp.length === 0 ? (
          <p className="p-4 text-sm text-ink-muted">
            None. Each RTMP encode is its own small channel fed from the ingest flows - started, stopped and
            re-keyed without touching the main encoder.
          </p>
        ) : (
          <div className="divide-y divide-grid">
            {data.rtmp.map((r) => (
              <div key={r.id} className="space-y-2 p-4">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-medium">{r.name}</span>
                  <StateBadge state={r.state} progress={r.progress} />
                  <span className="text-xs text-ink-muted">
                    {r.resolution}{r.fps} · H.264 · {r.mbps} Mbit · pair {r.audioPair} AAC {r.audioKbps}k
                    {c.estimate?.parts.find((p) => p.group === `rtmp:${r.id}`) && ` · ${formatUsd(c.estimate.parts.find((p) => p.group === `rtmp:${r.id}`)!.usdPerHour, 3)}/hr running`}
                  </span>
                  <span className="flex-1" />
                  <AwsLink href={r.console} what="channel" />
                  {r.state !== 'creating' && r.state !== 'missing' && (
                    <Button variant="ghost" onClick={() => setSettingsOf({ title: r.name, rtmpId: r.id })}>MediaLive settings</Button>
                  )}
                  {(r.state === 'stopped' || r.state === 'failed') && can('resource:start') && (
                    <Button variant="primary" disabled={rtmpMoving(r) || !running} onClick={() => void run(() => api.post(`/encoders/${id}/rtmp/${r.id}/start`), `Starting ${r.name}.`)}>
                      {r.state === 'failed' ? 'Retry' : 'Start'}
                    </Button>
                  )}
                  {r.state === 'running' && can('resource:stop') && (
                    <Button disabled={rtmpMoving(r)} onClick={() => setRtmpAsk({ r, action: 'stop' })}>Stop</Button>
                  )}
                  {can('resource:edit') && (
                    <>
                      <Button variant="ghost" disabled={rtmpMoving(r)} onClick={() => setRtmpEdit({ id: r.id, label: r.label, value: { ...r, destinations: r.destinations.map((d) => ({ ...d })) } })}>Edit</Button>
                      <Button variant="ghost" disabled={rtmpMoving(r)} onClick={() => setRtmpAsk({ r, action: 'delete' })}>Delete</Button>
                    </>
                  )}
                </div>
                {r.error && <div className="text-xs" style={{ color: 'var(--color-critical)' }}>{r.error}</div>}
                <Alerts alerts={r.alerts} />
                <div className="overflow-x-auto"><table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-grid text-left text-ink-muted">
                      <th className="py-1 pr-3 font-medium">Destination</th>
                      <th className="py-1 pr-3 font-medium">Stream key</th>
                      <th className="py-1 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.destinations.map((d, i) => (
                      <tr key={i} className="border-b border-grid last:border-0">
                        <td className="py-1.5 pr-3"><code className="break-all font-mono">{d.url}</code></td>
                        <td className="py-1.5 pr-3"><code className="font-mono">{showKeys ? d.streamKey : '•'.repeat(Math.min(12, d.streamKey.length))}</code></td>
                        <td className="py-1.5">
                          <span className="inline-flex items-center gap-1.5">
                            <Dot on={r.state === 'running' ? (r.alerts.length ? false : true) : null} />
                            <span className="text-ink-secondary">{r.state === 'running' ? (r.alerts.length ? 'MediaLive reports a problem' : 'sending') : r.state}</span>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
                {r.state === 'stopped' && !running && <div className="text-xs text-ink-muted">Start the encoder first - RTMP takes its picture from the ingest flows.</div>}
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        {data.preview && (
          <Card title="Cost per hour, running">
            <div className="p-4">{c.estimate ? <CostBreakdown est={c.estimate} /> : <span className="text-xs text-ink-muted">Pricing unavailable.</span>}</div>
          </Card>
        )}
        <Card title="Billed so far">
          <div className="space-y-2 p-4 text-sm">
            {c.billed.byResource.length === 0 ? (
              <p className="text-xs text-ink-muted">Nothing billed yet - Cost Explorer reports a day behind.</p>
            ) : c.billed.byResource.map((r) => (
              <div key={r.name} className="flex justify-between gap-3 text-xs">
                <span className="text-ink-secondary">{r.name}</span>
                <span className="tabular">{formatUsd(r.usd)}</span>
              </div>
            ))}
            <div className="flex justify-between gap-3 border-t border-grid pt-2">
              <span>Total billed</span><strong className="tabular">{formatUsd(c.billed.total)}</strong>
            </div>
            <p className="text-xs text-ink-muted">Every resource this encoder has had, including RTMP encodes since deleted. Kept after the encoder is deleted - see Costs › Encoders.</p>
          </div>
        </Card>
      </div>

      {/* --- Edit encoding --- */}
      <Modal
        open={!!editing && !!opts}
        onClose={() => setEditing(null)}
        title="Edit encoding"
        width="max-w-5xl"
        footer={
          <>
            <Button onClick={() => setEditing(null)}>Cancel</Button>
            <Button variant="primary" loading={acting} onClick={() => void run(async () => {
              await api.patch(`/encoders/${id}/encoding`, editing);
              setEditing(null);
            }, running ? 'Applying - the channel restarts, about two minutes.' : 'Saved.')}>
              {running ? 'Apply and restart the channel' : 'Save'}
            </Button>
          </>
        }
      >
        {editing && opts && (
          <div className="space-y-4">
            {running && (
              <Alert tone="warning" title="The main channel restarts">
                MediaLive only takes these while stopped. Outputs go black for a minute or two; the flows stay up, so
                receivers keep their connections, and RTMP encodes are not affected.
              </Alert>
            )}
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Audio pairs">
                <Select value={editing.audioPairs} onChange={(e) => setEditing({ ...editing, audioPairs: Number(e.target.value) })}>
                  {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
                </Select>
              </Field>
              <label className="flex items-center gap-2 pt-5 text-sm">
                <input type="checkbox" checked={editing.dashboardPreview} onChange={(e) => setEditing({ ...editing, dashboardPreview: e.target.checked })} />
                Dashboard preview
              </label>
              <label className="flex items-center gap-2 pt-5 text-sm">
                <input type="checkbox" checked={editing.overlayEnabled} onChange={(e) => setEditing({ ...editing, overlayEnabled: e.target.checked })} />
                HTML overlay enabled
              </label>
            </div>
            {editing.encodes.map((e, i) => {
              const set = (p: Partial<Encode>) => setEditing({ ...editing, encodes: editing.encodes.map((x, j) => (j === i ? { ...x, ...p } : x)) });
              return (
                <div key={i} className="space-y-2 rounded border border-grid p-3">
                  <div className="text-sm font-medium">{e.label} <span className="text-xs text-ink-muted">- name and flows are fixed once launched</span></div>
                  <div className="grid gap-3 sm:grid-cols-3"><PictureFields opts={opts} value={e} onChange={set} resolutions={opts.encodeResolutions} /></div>
                  <div className="grid gap-3 sm:grid-cols-4"><CodecFields opts={opts} value={e} onChange={set} /></div>
                </div>
              );
            })}
          </div>
        )}
      </Modal>

      {/* --- Clips --- */}
      <Modal
        open={!!clipEdit}
        onClose={() => setClipEdit(null)}
        title="Media clips"
        width="max-w-2xl"
        footer={
          <>
            <Button onClick={() => setClipEdit(null)}>Cancel</Button>
            <Button variant="primary" loading={acting} disabled={clipUploading} onClick={() => void run(async () => {
              await api.put(`/encoders/${id}/media`, { media: clipEdit });
              setClipEdit(null);
            }, running ? 'Applying - the channel restarts, about two minutes.' : 'Saved.')}>
              {clipUploading ? 'Waiting for uploads…' : running ? 'Apply and restart the channel' : 'Save'}
            </Button>
          </>
        }
      >
        {clipEdit && (
          <div className="space-y-3 text-sm">
            {running && (
              <Alert tone="warning" title="The main channel restarts">
                Each clip is a MediaLive input, and inputs only change on a stopped channel. Outputs go black for a
                minute or two; the flows stay up.
              </Alert>
            )}
            <p className="text-xs text-ink-muted">MP4 with H.264 or HEVC video and AAC audio, up to 5 GB each. Removed clips are deleted from storage.</p>
            <ClipUploader value={clipEdit} onChange={setClipEdit} onBusy={setClipUploading} />
          </div>
        )}
      </Modal>

      {/* --- Ingest port --- */}
      <Modal
        open={!!portEdit}
        onClose={() => setPortEdit(null)}
        title="Change ingest port"
        footer={
          <>
            <Button onClick={() => setPortEdit(null)}>Cancel</Button>
            <Button variant="primary" loading={acting} onClick={() => void run(async () => {
              await api.patch(`/encoders/${id}/ingest/${portEdit!.index}`, { port: portEdit!.port });
              setPortEdit(null);
            }, 'Port changed - the venue has to send to the new port.')}>Change port</Button>
          </>
        }
      >
        {portEdit && (
          <div className="space-y-3 text-sm">
            <p>The address stays the same; only the port changes. A feed arriving now drops until the venue sends to the new port.</p>
            <Field label="Port (1024-65535)">
              <Input type="number" min={1024} max={65535} value={portEdit.port} onChange={(e) => setPortEdit({ ...portEdit, port: Number(e.target.value) })} autoFocus />
            </Field>
          </div>
        )}
      </Modal>

      {/* --- RTMP add / edit --- */}
      <Modal
        open={!!rtmpEdit && !!opts}
        onClose={() => setRtmpEdit(null)}
        title={rtmpEdit?.id ? `Edit ${rtmpEdit.value.label}` : 'Add an RTMP encode'}
        width="max-w-5xl"
        footer={
          <>
            <Button onClick={() => setRtmpEdit(null)}>Cancel</Button>
            <Button variant="primary" loading={acting} onClick={() => void run(async () => {
              if (rtmpEdit!.id) await api.patch(`/encoders/${id}/rtmp/${rtmpEdit!.id}`, { rtmp: rtmpEdit!.value });
              else await api.post(`/encoders/${id}/rtmp`, { rtmp: rtmpEdit!.value, start: running });
              setRtmpEdit(null);
            }, rtmpEdit?.id ? 'Updating - it restarts if it was running.' : running ? 'Creating and starting - about three minutes.' : 'Creating.')}>
              {rtmpEdit?.id ? 'Save' : running ? 'Create and start' : 'Create'}
            </Button>
          </>
        }
      >
        {rtmpEdit && opts && (
          <RtmpEditor opts={opts} value={rtmpEdit.value} audioPairs={data.spec.audioPairs} originalLabel={rtmpEdit.label}
            onChange={(v) => setRtmpEdit({ ...rtmpEdit, value: v })} />
        )}
      </Modal>

      <ChannelSettings encoderId={data.id} target={settingsOf} onClose={() => setSettingsOf(null)} />

      {/* --- Save as template --- */}
      <Modal
        open={!!saveTpl}
        onClose={() => setSaveTpl(null)}
        title={saveTpl?.savedId ? 'Template saved' : 'Save as template'}
        footer={saveTpl?.savedId ? (
          <>
            <Button onClick={() => setSaveTpl(null)}>Close</Button>
            <Button variant="primary" onClick={() => navigate(`/launch/encoder?template=${saveTpl.savedId}`)}>Launch one like this</Button>
          </>
        ) : (
          <>
            <Button onClick={() => setSaveTpl(null)}>Cancel</Button>
            <Button variant="primary" loading={acting} disabled={!saveTpl || saveTpl.name.trim().length < 2} onClick={() => void run(async () => {
              const r = await api.post<{ id: string }>(`/encoders/${id}/template`, { name: saveTpl!.name, description: saveTpl!.description });
              setSaveTpl({ ...saveTpl!, savedId: r.id });
            })}>Save template</Button>
          </>
        )}
      >
        {saveTpl && (saveTpl.savedId ? (
          <p className="text-sm">
            "{saveTpl.name}" is on the launch page's template list. Pick it there to fill the form in, then put in
            the name, receivers' addresses and stream keys.
          </p>
        ) : (
          <div className="space-y-3 text-sm">
            {error && <Alert tone="critical">{error}</Alert>}
            <p className="text-xs text-ink-muted">
              Keeps the encoder's name, encodes, codecs and profiles, audio, ingest ports, every flow's outputs with
              their names and ports, and the RTMP encodes. Receivers' addresses and RTMP servers become 0.0.0.0,
              stream keys and the overlay page are left blank, and clips are not kept - each is filled in at launch.
            </p>
            <Field label="Name">
              <Input value={saveTpl.name} maxLength={80} onChange={(e) => setSaveTpl({ ...saveTpl, name: e.target.value })} autoFocus />
            </Field>
            <Field label="Description">
              <textarea
                className="w-full rounded-md bg-plane px-3 py-2 text-sm text-ink ring-1 ring-hairline placeholder:text-ink-muted"
                rows={4} maxLength={1000}
                placeholder="What it is for - e.g. golf, two cameras, TVNZ and Sky pull, YouTube"
                value={saveTpl.description}
                onChange={(e) => setSaveTpl({ ...saveTpl, description: e.target.value })}
              />
            </Field>
          </div>
        ))}
      </Modal>

      <Confirm
        open={stopAsk}
        title={`Stop ${data.name}?`}
        message={`Every output stops: receivers pulling from its flows${data.rtmp.length ? ', and every RTMP encode,' : ''} lose the feed until it is started again. Starting takes a minute or two.`}
        confirmLabel="Stop the encoder"
        danger
        busy={acting}
        onClose={() => setStopAsk(false)}
        onConfirm={() => { setStopAsk(false); void run(() => api.post(`/encoders/${id}/stop`)); }}
      />
      <Confirm
        open={!!encodeAsk}
        title={`Remove the ${encodeAsk?.label ?? ''} encode?`}
        message={`The encode comes off the channel and its flows (${encodeAsk?.flows.join(', ') ?? ''}) are deleted, with every receiver on them. Their addresses are gone for good.${running ? ' The channel restarts to take the change: a minute or two of black on every other output.' : ''}`}
        confirmLabel="Remove"
        danger
        onClose={() => setEncodeAsk(null)}
        onConfirm={() => { const a = encodeAsk!; setEncodeAsk(null); void run(() => api.delete(`/encoders/${id}/encodes/${a.encode}`), `Removing ${a.label}.`); }}
      />
      <Confirm
        open={!!rtmpAsk}
        title={rtmpAsk?.action === 'delete' ? `Delete ${rtmpAsk.r.name}?` : `Stop ${rtmpAsk?.r.name ?? ''}?`}
        message={rtmpAsk?.action === 'delete'
          ? 'Its destinations stop receiving and the channel is removed. The encoder itself carries on.'
          : 'Its destinations stop receiving until it is started again. The encoder itself carries on.'}
        confirmLabel={rtmpAsk?.action === 'delete' ? 'Delete' : 'Stop'}
        danger
        onClose={() => setRtmpAsk(null)}
        onConfirm={() => {
          const a = rtmpAsk!; setRtmpAsk(null);
          if (a.action === 'delete') void run(() => api.delete(`/encoders/${id}/rtmp/${a.r.id}`), `Deleting ${a.r.name}.`);
          else void run(() => api.post(`/encoders/${id}/rtmp/${a.r.id}/stop`), `Stopping ${a.r.name}.`);
        }}
      />

      {/* --- Rename: a rebuild under the new name --- */}
      <Modal
        open={renameAsk}
        onClose={() => setRenameAsk(false)}
        title={`Rename ${data.name}`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setRenameAsk(false)}>Cancel</Button>
            <Button
              variant="danger"
              loading={acting}
              disabled={!newName.trim() || newName.trim().toUpperCase() === data.name}
              onClick={() => { setRenameAsk(false); void run(() => api.post(`/encoders/${id}/rename`, { name: newName.trim() }), 'Renaming - rebuilding the encoder under its new name.'); }}
            >
              Rename and rebuild
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm">
          <Field label="New name"><Input autoFocus value={newName} maxLength={40} onChange={(e) => setNewName(e.target.value.toUpperCase())} /></Field>
          <Alert tone="warning" title="Renaming rebuilds the encoder">
            The name is in every AWS resource it has, and none of them can be renamed in place. Its flows, channel, inputs
            and RTMP encodes are deleted and made again under the new name, which takes several minutes. <b>Every
            address changes</b>: the ingest address your encoder sends to, and every output address receivers pull from,
            so they all need repointing. Settings, clips, the preview and the cost history stay. A recorder attached
            to it is removed.
          </Alert>
        </div>
      </Modal>

      {/* --- Delete --- */}
      <Modal
        open={deleting}
        onClose={() => setDeleting(false)}
        title={`Delete ${data.name}`}
        footer={
          <>
            <Button onClick={() => setDeleting(false)}>Cancel</Button>
            <Button
              variant="danger"
              onClick={async () => {
                try {
                  await api.delete(`/encoders/${data.id}`, { confirmName: data.name });
                  setDeleting(false);
                  await load();
                } catch (err) {
                  setError(err instanceof ApiError ? err.message : 'Could not delete.');
                  setDeleting(false);
                }
              }}
            >
              Delete everything
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm">
          <Alert tone="critical" title="This cannot be undone">
            {data.state === 'running' ? 'It is on air now. ' : ''}
            The MediaLive channel, its inputs, every RTMP encode and all{' '}
            {data.ingest.length + data.outputs.reduce((n, o) => n + o.flows.length, 0)} flows it made are stopped and
            deleted, including every address a venue sends to or a receiver pulls from. Clips uploaded for it go too.
          </Alert>
          <p className="text-ink-secondary">
            Kept: its cost history, and any flow it was added onto (that stays its own). To keep the encoder but stop
            paying for it, <b>Stop</b> it instead: a stopped encoder costs nothing to keep and starts again in a minute
            or two with every address unchanged.
          </p>
        </div>
      </Modal>
      {data.state === 'deleted' && (
        <Alert tone="info">
          Deleted. <button className="underline" onClick={() => navigate('/encoders')}>Back to encoders</button>
        </Alert>
      )}
    </div>
  );
}
